/**
 * Shared nutrition constants and helpers used by DietRecallTab and MealPlanTab.
 *
 * Kept as a non-tsx module so React Fast Refresh can hot-reload the
 * component files that consume these without invalidation.
 */
import type { MealTimeCode } from "@/types/nutrition";

export const MEAL_TIME_DISPLAY: Record<string, string> = {
  EM: "Early Morning",
  PB: "Pre-Breakfast",
  BR: "Breakfast",
  MM: "Mid-Morning",
  LU: "Lunch",
  EV: "Evening",
  DI: "Dinner",
  PW: "Pre-Workout",
  PO: "Post-Workout",
};

// Reference order for meal times (Flutter parity).
export const MEAL_TIME_ORDER: MealTimeCode[] = [
  "EM",
  "PB",
  "BR",
  "MM",
  "LU",
  "EV",
  "DI",
  "PW",
  "PO",
];

// Macro → kcal conversion helpers (matching Flutter controller calculations).
export const carbsGramsToKcal = (grams: number) => grams * 4;
export const proteinsGramsToKcal = (grams: number) => grams * 4;
export const fatsGramsToKcal = (grams: number) => grams * 9;
export const fiberGramsToKcal = (grams: number) => grams * 2;

// Atwater 2-factor energy from per-macro grams. Meal Plan uses net carbs
// (carbs minus fiber) so fiber is counted once at 2 kcal/g; Diet Recall
// keeps gross carbs to match the food database's reported energy.
//
// Used everywhere a kcal total is displayed (row, group header, pie centre,
// targets) so all four values reconcile against the same numbers.
export const computeAtwaterKcal = (
  macros: {
    carbs?: number;
    protein?: number;
    fat?: number;
    fiber?: number;
  },
  options: { useNetCarbs?: boolean } = {}
) => {
  const { useNetCarbs = false } = options;
  const carbs = macros.carbs ?? 0;
  const protein = macros.protein ?? 0;
  const fat = macros.fat ?? 0;
  const fiber = macros.fiber ?? 0;
  const carbsForKcal = useNetCarbs ? Math.max(0, carbs - fiber) : carbs;
  return carbsForKcal * 4 + protein * 4 + fiber * 2 + fat * 9;
};

// Per-entry kcal — `calculated_energy` is the backend's ground truth
// (matches what the Add Food dialog showed at the time of save).
// Used as the single source of truth for every kcal display so they
// reconcile against each other.
export const computeEntryKcal = (entry: {
  calculated_energy?: number;
}): number => entry.calculated_energy ?? 0;

// Quantity / feed volume inputs allow at most one decimal digit. Used to
// validate keystrokes — does not round existing values, just blocks typing
// a second decimal (e.g. "1.2" allowed, "1.22" rejected). Permits the
// trailing-dot intermediate state ("1.") so users can keep typing.
export const QUANTITY_INPUT_PATTERN = /^\d*(?:\.\d?)?$/;

// A food built from something indivisible — a capsule, a melt strip — can only
// be logged in whole servings. Nutrients scale linearly, so half a shake would
// report half a capsule; nobody takes half a capsule, so a fraction is wrong in
// both directions rather than approximately right. The food's
// `requires_whole_servings` flag says which, and it is keyed on the missing
// weight and NOT on food type: a whey scoop is a supplement that halves
// perfectly well, so a milk-and-whey shake stays fully divisible.
export const WHOLE_QUANTITY_INPUT_PATTERN = /^\d*$/;

export const quantityPatternFor = (requiresWholeServings?: boolean): RegExp =>
  requiresWholeServings ? WHOLE_QUANTITY_INPUT_PATTERN : QUANTITY_INPUT_PATTERN;

// Helper to read a nutrient amount/unit/% from the new API category shape:
//   { "Nutrient Name": { amount, unit, rda_percentage, ... } }
export const getNutrientFromCategory = (
  category:
    | Record<
        string,
        { amount: number; unit: string; rda_percentage: number | null }
      >
    | undefined,
  nutrientName: string
) => {
  if (!category) return null;
  return category[nutrientName] || null;
};
